import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadIcon       from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon     from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LockIcon              from "@mui/icons-material/Lock";

// Import shared preview components — NOT re-defined inline (inline causes flicker)
import { PdfPreview, ImagePreviewBlock } from "./BillPreviewComponents";

const isPdfFile = (file) => file?.type === "application/pdf";

export default function FileUploadField({
  cardData,
  imagePreview,
  previewFile,
  formikField,
  required,
  locked = false,
  onFileSelect,
  onFileRemove,
  onDownloadPdf,    // always pass these — even when locked
  onOpenPdfNewTab,  // PdfPreview has its own fallback if undefined
}) {
  const color   = cardData?.color   || "#6366F1";
  const bgColor = cardData?.bgColor || "#f8fafc";
  const ltColor = cardData?.lightColor || "#e0e7ff";

  return (
    <Box
      sx={{
        p: 2.5, borderRadius: 3,
        backgroundColor: locked ? "#f1f5f9" : "#f8fafc",
        border: `2px solid ${locked ? "#cbd5e1" : "#e2e8f0"}`,
        transition: "all 0.2s",
        ...(!locked && {
          "&:focus-within": {
            borderColor: color,
            backgroundColor: "#fff",
            boxShadow: `0 0 0 3px ${color}20`,
          },
        }),
      }}
    >
      {/* Label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AddPhotoAlternateIcon sx={{ fontSize: 20, color: locked ? "#94a3b8" : color }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: locked ? "#64748b" : "#0f172a", fontSize: "0.875rem" }}
        >
          Bill Image / PDF{required && !locked ? " *" : !locked ? " (Optional)" : ""}
        </Typography>
        {locked && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: 0.5 }}>
            <LockIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.7rem" }}>
              Auto-attached
            </Typography>
          </Box>
        )}
      </Box>

      {/* Upload area — only when no file and not locked */}
      {!imagePreview && !locked && (
        <Paper
          component="label"
          sx={{
            border: `2px dashed ${formikField?.touched && formikField?.error ? "#ef4444" : "#e2e8f0"}`,
            borderRadius: 3, minHeight: 220,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 2,
            background: `linear-gradient(135deg, ${bgColor} 0%, #ffffff 100%)`,
            cursor: "pointer", transition: "all 0.3s ease",
            "&:hover": {
              borderColor: color,
              transform: "translateY(-2px)",
              boxShadow: `0 8px 16px -4px ${color}30`,
            },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 56, color }} />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
              {required ? "Upload Bill" : "Attach Bill (Optional)"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Click to browse • Images (JPG, PNG) or PDF
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {["JPG", "PNG", "PDF"].map((ext) => (
              <Chip key={ext} label={ext} size="small"
                sx={{ backgroundColor: ltColor, color, fontWeight: 600, fontSize: "0.7rem" }} />
            ))}
          </Stack>
          <input
            hidden type="file" accept="image/*,application/pdf"
            onChange={(e) => { const f = e.target.files[0]; if (f && onFileSelect) onFileSelect(f); }}
          />
        </Paper>
      )}

      {/* Preview — PDF or Image */}
      {imagePreview && (
        <Box>
          {isPdfFile(previewFile) ? (
            // PdfPreview has built-in fallback handlers — safe to pass undefined
            <PdfPreview
              url={imagePreview}
              color={color}
              onDownload={onDownloadPdf}
              onOpenNewTab={onOpenPdfNewTab}
            />
          ) : (
            <ImagePreviewBlock url={imagePreview} color={color} />
          )}

          {/* Change / Remove — only when not locked */}
          {!locked && (
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
              <Button
                component="label" fullWidth variant="outlined"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 600,
                  borderColor: color, color, borderWidth: 2,
                  "&:hover": { borderWidth: 2, borderColor: color, backgroundColor: bgColor },
                }}
              >
                Change File
                <input hidden type="file" accept="image/*,application/pdf"
                  onChange={(e) => { const f = e.target.files[0]; if (f && onFileSelect) onFileSelect(f); }} />
              </Button>
              <Button
                fullWidth variant="outlined" color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={onFileRemove}
                sx={{ borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 600, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
              >
                Remove
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {/* Validation error */}
      {formikField?.touched && formikField?.error && (
        <Typography color="error" variant="caption" sx={{ display: "block", mt: 1, ml: 0.5 }}>
          {formikField.error}
        </Typography>
      )}
    </Box>
  );
}